// The newsletter's authoring format, and the two bodies it renders to.
//
// Nobody writes HTML. An issue is written as plain text — the same way the
// check-in emails always have been — plus two markers:
//
//   ## Section heading
//   [[image: chart.png | what the chart shows]]
//
// Blank lines separate paragraphs. Everything else is prose. That is the whole
// language, deliberately: an admin can hold it in their head, and there is
// nothing in it that can fail silently in front of the whole list.
//
// Both bodies come from here so the preview in the admin screen and the message
// that actually sends cannot drift apart — which is the only thing that makes a
// preview worth having.
//
// Most mail clients block remote images by default, so the alt text is not a
// nicety: for a good share of readers it is the picture. An image whose alt text
// says nothing is an image that says nothing.

import { renderTemplate } from './bulkEmail';

export type EmailFormat = 'text' | 'html';

/**
 * The postal address commercial email has to carry.
 *
 * Lives here rather than only in the Netlify function so the admin preview
 * renders the same footer that sends. netlify/lib/mailingLists.js imports it and
 * lets COMPANY_POSTAL_ADDRESS override it at run time.
 */
export const DEFAULT_POSTAL_ADDRESS =
  'eTherm Solutions LLC · 160 E Main St, Richmond, VT 05477';

export interface ComposeOptions {
  format: EmailFormat;
  /** Used for {{first_name}}. */
  fullName: string | null;
  /** Per-recipient one-click unsubscribe URL. */
  optOutUrl: string;
  /** Required in commercial mail; empty for the check-in list. */
  postalAddress: string;
  /** Origin that relative image names resolve against, no trailing slash. */
  siteBase: string;
  /**
   * Appended to each hosted image URL as ?v=. The sender passes the batch id so
   * every send uses image URLs no mail client or image proxy has seen before:
   * one that fetched the address before the file was live keeps serving that
   * failure, and Apple's Mail Privacy Protection proxy did exactly that on
   * 2026-10-06.
   */
  imageVersion?: string;
}

export interface ComposedEmail {
  text: string;
  /** Null for a plain-text send, so the caller can omit the field entirely. */
  html: string | null;
}

// ─── Parsing ─────────────────────────────────────────────────────────────────

export type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'image'; src: string; alt: string }
  | { kind: 'paragraph'; text: string };

const IMAGE_MARKER = /^\[\[image:\s*([^|\]]+?)\s*(?:\|\s*([^\]]*?)\s*)?\]\]$/i;

/**
 * Resolves an image name to a URL.
 *
 * A bare filename is served from the app's own /newsletter/ directory, which is
 * what makes adding a picture a matter of dropping a file in public/newsletter/
 * rather than arranging hosting. A full URL is passed through so a chart that
 * already lives somewhere does not have to be copied.
 */
export function imageUrl(src: string, siteBase: string, version?: string): string {
  if (/^https?:\/\//i.test(src)) return src;
  const url = `${siteBase.replace(/\/$/, '')}/newsletter/${src.replace(/^\/+/, '')}`;
  return version ? `${url}?v=${encodeURIComponent(version)}` : url;
}

export function parseBlocks(body: string): Block[] {
  const blocks: Block[] = [];

  for (const chunk of body.replace(/\r\n/g, '\n').split(/\n\s*\n/)) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;

    // A heading or an image is a block on its own. Anything sharing its chunk is
    // prose that happens to sit next to it, and stays prose.
    const lines = trimmed.split('\n');
    if (lines.length === 1) {
      const line = lines[0].trim();

      const image = line.match(IMAGE_MARKER);
      if (image) {
        blocks.push({ kind: 'image', src: image[1].trim(), alt: (image[2] || '').trim() });
        continue;
      }

      if (line.startsWith('## ')) {
        blocks.push({ kind: 'heading', text: line.slice(3).trim() });
        continue;
      }
    }

    blocks.push({ kind: 'paragraph', text: trimmed });
  }

  return blocks;
}

// ─── Rendering ───────────────────────────────────────────────────────────────

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const BRAND_BLUE = '#185FA5';
const INK = '#1a1a1a';
const MUTED = '#6b7280';
const RULE = '#e5e7eb';
const PAGE_BG = '#f4f4f2';

function textBlocks(blocks: Block[], siteBase: string, version?: string): string {
  return blocks
    .map(b => {
      if (b.kind === 'heading') return b.text.toUpperCase();
      // Images cannot travel in the text part, so it carries the alt text and a
      // link — which is also what a reader with images switched off gets.
      if (b.kind === 'image') {
        const label = b.alt || 'Image';
        return `[ ${label}: ${imageUrl(b.src, siteBase, version)} ]`;
      }
      return b.text;
    })
    .join('\n\n');
}

function htmlBlocks(blocks: Block[], siteBase: string, version?: string): string {
  return blocks
    .map(b => {
      if (b.kind === 'heading') {
        return `<h2 style="margin:32px 0 12px;font-size:19px;line-height:1.3;color:${INK};font-weight:700;">${escapeHtml(b.text)}</h2>`;
      }
      if (b.kind === 'image') {
        const url = escapeHtml(imageUrl(b.src, siteBase, version));
        const alt = escapeHtml(b.alt);
        const caption = b.alt
          ? `<div style="margin:8px 0 0;font-size:13px;line-height:1.5;color:${MUTED};">${alt}</div>`
          : '';
        return (
          `<div style="margin:24px 0;">` +
          `<img src="${url}" alt="${alt}" width="560" ` +
          `style="display:block;width:100%;max-width:560px;height:auto;border:1px solid ${RULE};border-radius:6px;">` +
          caption +
          `</div>`
        );
      }
      // A single newline inside a paragraph is a deliberate line break.
      const inner = escapeHtml(b.text).replace(/\n/g, '<br>');
      return `<p style="margin:0 0 16px;font-size:16px;line-height:1.65;color:${INK};">${inner}</p>`;
    })
    .join('\n');
}

function textFooter(optOutUrl: string, postalAddress: string): string {
  const lines = ['--', 'Jake Marin · TrueLoad · app.trueloadapp.com', 'Reply to this email to reach me directly.'];
  if (postalAddress) lines.push(postalAddress);
  lines.push(`Unsubscribe: ${optOutUrl}`);
  return lines.join('\n');
}

function htmlFooter(optOutUrl: string, postalAddress: string): string {
  const address = postalAddress
    ? `<div style="margin:0 0 6px;">${escapeHtml(postalAddress)}</div>`
    : '';
  return (
    `<div style="margin:36px 0 0;padding:18px 0 0;border-top:1px solid ${RULE};font-size:12px;line-height:1.6;color:${MUTED};">` +
    `<div style="margin:0 0 6px;">Jake Marin · TrueLoad · ` +
    `<a href="https://app.trueloadapp.com" style="color:${MUTED};">app.trueloadapp.com</a></div>` +
    `<div style="margin:0 0 6px;">Reply to this email to reach me directly.</div>` +
    address +
    `<div><a href="${escapeHtml(optOutUrl)}" style="color:${MUTED};">Unsubscribe</a></div>` +
    `</div>`
  );
}

/**
 * The email shell.
 *
 * Tables and inline styles, because Outlook still ignores most of a stylesheet.
 * The wordmark is live text rather than a logo image, so a reader with images
 * blocked still sees who the mail is from.
 */
function shell(inner: string): string {
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:${PAGE_BG};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${PAGE_BG};">
  <tr><td align="center" style="padding:28px 12px;">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0"
      style="width:100%;max-width:600px;background:#ffffff;border-radius:10px;">
      <tr><td style="padding:28px 32px 34px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;">
        <div style="margin:0 0 22px;font-size:15px;font-weight:700;letter-spacing:0.3px;color:${BRAND_BLUE};">TrueLoad</div>
${inner}
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;
}

export function composeEmail(template: string, opts: ComposeOptions): ComposedEmail {
  const filled = renderTemplate(template, opts.fullName);
  const blocks = parseBlocks(filled);

  const text = [textBlocks(blocks, opts.siteBase, opts.imageVersion), '', textFooter(opts.optOutUrl, opts.postalAddress)].join('\n');

  if (opts.format !== 'html') return { text, html: null };

  return {
    text,
    html: shell(htmlBlocks(blocks, opts.siteBase, opts.imageVersion) + htmlFooter(opts.optOutUrl, opts.postalAddress)),
  };
}
